import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import Link from 'UIKit/Link'
import Text from 'UIKit/Text'
import { RouteConfig } from 'config/constants/route'
import { useRouter } from 'hooks/useRouter'
import { RowCenter } from 'layout/Components/Row'
import React from 'react'
import { Trans } from 'react-i18next'
import { useCloseMenu, useMenuContainer } from 'state/app/hooks'
import { useAuth } from 'state/auth/hooks'
import { useUnwatchedNewBonusListener } from 'state/bonus/hooks'
import { useUserBonus } from 'state/bonus/hooks/useUserBonus'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import { StyledText, StyledWrapIcon, StyledWrapTab } from './styled'

const StyledBonusCenterBadge = styled(RowCenter)<{ $isHaveNewBonus: boolean }>`
  background: ${({ theme: { colors } }) => colors.strokeAlt3};
  padding: 2px 4px;
  border-radius: 40px;

  min-width: 18px;
  min-height: 16px;
  font-size: 10px;
  font-weight: 600;

  position: absolute;
  right: 0;
  top: 0;
  transform: translate(60%, -30%);

  ${({ $isHaveNewBonus }) =>
    $isHaveNewBonus &&
    css`
      @keyframes ping {
        0% {
          transform: scale(1);
          opacity: 0.5;
        }
        100% {
          transform: scale(2);
          opacity: 0;
        }
      }

      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 40px;
        background: #fff;
        animation: ping 1.7s ease infinite;
        opacity: 0.25;
        z-index: 10;
      }
    `};
`

const StyledNotificationContainer = styled(Flex)`
  width: max-content;
  background: ${({ theme }) => theme.colors.textTertiary} !important;
  border-radius: ${({ theme }) => theme.radii.tiny};
  gap: 8px;
  padding: 4px 8px;
  z-index: 6;
  position: absolute;
  top: -40px;
  left: -200px;
  line-height: 20px;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    bottom: -5px;
    right: 124px;
    width: 10px;
    height: 10px;
    transform: rotate(135deg);
    border-radius: 1px;
    background: ${({ theme }) => theme.colors.textTertiary} !important;
    backdrop-filter: blur(6px);
    z-index: -2;
  }

  display: flex;

  ${({ theme }) => theme.mediaQueries.md} {
    display: none;
  }
`

const NotificationBonus = () => {
  const [unwatchedNewBonus, onClose] = useUnwatchedNewBonusListener()
  const { isSigned } = useAuth()
  const closeMenu = useCloseMenu()

  return (
    unwatchedNewBonus &&
    isSigned && (
      <StyledNotificationContainer
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          closeMenu()
        }}
      >
        <Text color="#161C24 !important" fontSize="12px" fontWeight={500}>
          <Trans>You have received a new reward, check it out now!</Trans>
        </Text>
        <Flex
          alignItems="center"
          justifyContent="center"
          width="12px"
          height="12px"
          style={{ cursor: 'pointer' }}
          onClick={onClose}
        >
          <Icons.ClearIcon width="12px" height="12px" />
        </Flex>
      </StyledNotificationContainer>
    )
  )
}

const BonusItem = () => {
  const { bonuses } = useUserBonus()
  const [unwatchedNewBonus, onClose] = useUnwatchedNewBonusListener()
  const closeMenu = useCloseMenu()

  const { asPath } = useRouter()
  const { isMenuOpen } = useMenuContainer()

  return (
    <StyledWrapTab
      id="bottom-bar-bonus"
      as={Link}
      href={RouteConfig.BonusCenter}
      onClick={() => {
        onClose()
        closeMenu()
      }}
    >
      <Box width="24px" position="relative">
        <NotificationBonus />
        <StyledWrapIcon $active={!isMenuOpen && asPath === RouteConfig.BonusCenter}>
          <Icons.BonusIcon />
        </StyledWrapIcon>

        {!!bonuses?.length && (
          <StyledBonusCenterBadge $isHaveNewBonus={unwatchedNewBonus}>{bonuses.length}</StyledBonusCenterBadge>
        )}
      </Box>
      <StyledText $active={!isMenuOpen && asPath === RouteConfig.BonusCenter}>
        <Trans>Bonus</Trans>
      </StyledText>
    </StyledWrapTab>
  )
}

export default React.memo(BonusItem)
